import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Chip,
  Stack,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  AccountBalance as BankIcon,
  CloudSync as CloudSyncIcon,
  ListAlt as ListAltIcon,
  UploadFile as UploadFileIcon,
  VerifiedUser as VerifiedUserIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { 
  GET_BANK_ACCOUNTS_BY_COMPANY 
} from '../graphql/queries';
import {
  CREATE_BANK_ACCOUNT,
  UPDATE_BANK_ACCOUNT,
  SET_DEFAULT_BANK_ACCOUNT,
  ACTIVATE_BANK_ACCOUNT,
  DEACTIVATE_BANK_ACCOUNT,
} from '../graphql/mutations';

const CURRENCY_OPTIONS = [
  { code: 'BGN', name: 'Български лев' },
  { code: 'EUR', name: 'Евро' },
  { code: 'USD', name: 'Американски долар' },
  { code: 'GBP', name: 'Британска лира' },
  { code: 'CHF', name: 'Швейцарски франк' },
];

const TransactionDialog = ({ open, onClose, bankAccount, companyId }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [fromDate, setFromDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [file, setFile] = useState(null);
  const [importBankType, setImportBankType] = useState('UNICREDIT');

  const fetchTransactions = async () => {
    if (!bankAccount) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/bank/transactions?bankAccountId=${bankAccount.id}&fromDate=${fromDate}&toDate=${toDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      } else {
        console.error("Failed to load transactions");
      }
    } catch (e) {
      console.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && bankAccount) {
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bankAccount, fromDate, toDate]);

  const handleSyncWise = async () => {
    setSyncLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/bank/wise/sync?companyId=${companyId}&bankAccountId=${bankAccount.id}&fromDate=${fromDate}&toDate=${toDate}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const count = await response.json();
        alert(`Synced ${count} transactions`);
        fetchTransactions();
      } else {
        const err = await response.text();
        alert(`Error: ${err}`);
      }
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleImportXml = async () => {
    if (!file) return;
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('bankAccountId', bankAccount.id);
      formData.append('bankType', importBankType);

      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/bank/import/xml', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const count = await response.json();
        alert(`Imported ${count} transactions`);
        fetchTransactions();
        setFile(null);
      } else {
        const err = await response.text();
        alert(`Error: ${err}`);
      }
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Транзакции - {bankAccount?.bankName} ({bankAccount?.currencyCode})</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="От дата"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="До дата"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <Button variant="outlined" onClick={fetchTransactions} disabled={loading}>
            Обнови
          </Button>

          {bankAccount?.isWiseConnected && (
            <Button 
              variant="contained" 
              color="info" 
              startIcon={syncLoading ? <CircularProgress size={20} /> : <CloudSyncIcon />}
              onClick={handleSyncWise}
              disabled={syncLoading}
            >
              Wise Sync
            </Button>
          )}
        </Box>

        <Box sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>Ръчен импорт (XML)</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Банка</InputLabel>
              <Select value={importBankType} label="Банка" onChange={(e) => setImportBankType(e.target.value)}>
                <MenuItem value="UNICREDIT">UniCredit</MenuItem>
                <MenuItem value="POSTBANK">Postbank</MenuItem>
                <MenuItem value="OBB">OBB (Generic)</MenuItem>
              </Select>
            </FormControl>
            <input
              type="file"
              accept=".xml"
              onChange={(e) => setFile(e.target.files[0])}
            />
            <Button 
              variant="contained" 
              startIcon={importLoading ? <CircularProgress size={20} /> : <UploadFileIcon />}
              onClick={handleImportXml}
              disabled={!file || importLoading}
            >
              Импорт
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                <TableCell>Сума</TableCell>
                <TableCell>Описание</TableCell>
                <TableCell>Контрагент</TableCell>
                <TableCell>Тип</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((tx) => (
                <TableRow key={tx.id}>
                  <TableCell>{tx.date}</TableCell>
                  <TableCell sx={{ color: 'green', fontWeight: 'bold' }}>
                    {tx.amount} {bankAccount?.currencyCode}
                  </TableCell>
                  <TableCell>{tx.description}</TableCell>
                  <TableCell>{tx.contraName}</TableCell>
                  <TableCell>{tx.importSource}</TableCell>
                </TableRow>
              ))}
              {transactions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">Няма намерени транзакции</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Затвори</Button>
      </DialogActions>
    </Dialog>
  );
};

const BankAccountsTab = ({ companyId }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [transactionsDialogOpen, setTransactionsDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [wiseSyncing, setWiseSyncing] = useState(false);
  const [certificateUploading, setCertificateUploading] = useState(false);
  const [certificateFile, setCertificateFile] = useState(null);

  const [formData, setFormData] = useState({
    bankName: '',
    iban: '',
    bic: '',
    currencyCode: 'BGN',
    accountName: '',
    sortOrder: 0,
    description: '',
    isDefault: false,
  });
  const [errors, setErrors] = useState({});

  const { data, loading, error, refetch } = useQuery(GET_BANK_ACCOUNTS_BY_COMPANY, {
    variables: { companyId },
  });

  const [createBankAccount] = useMutation(CREATE_BANK_ACCOUNT, {
    onCompleted: () => {
      refetch();
      handleCloseDialog();
    },
  });

  const [updateBankAccount] = useMutation(UPDATE_BANK_ACCOUNT, {
    onCompleted: () => {
      refetch();
      handleCloseDialog();
    },
  });

  const [setDefaultBankAccount] = useMutation(SET_DEFAULT_BANK_ACCOUNT, {
    onCompleted: () => refetch(),
  });

  const [activateBankAccount] = useMutation(ACTIVATE_BANK_ACCOUNT, {
    onCompleted: () => refetch(),
  });

  const [deactivateBankAccount] = useMutation(DEACTIVATE_BANK_ACCOUNT, {
    onCompleted: () => refetch(),
  });

  const bankAccounts = data?.bankAccountsByCompany || [];

  const handleOpenDialog = (account) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        bankName: account.bankName,
        iban: account.iban,
        bic: account.bic,
        currencyCode: account.currencyCode,
        accountName: account.accountName || '',
        sortOrder: account.sortOrder || 0,
        description: account.description || '',
        isDefault: account.isDefault,
      });
    } else {
      setEditingAccount(null);
      setFormData({
        bankName: '',
        iban: '',
        bic: '',
        currencyCode: 'BGN',
        accountName: '',
        sortOrder: 0,
        description: '',
        isDefault: false,
      });
    }
    setDialogOpen(true);
    setErrors({});
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAccount(null);
    setFormData({});
    setErrors({});
  };

  const handleImportWiseBalances = async () => {
    setWiseSyncing(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/bank/wise/import-balances?companyId=${companyId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        refetch();
        alert('Wise balances imported successfully');
      } else {
        const err = await response.text();
        alert(`Error importing Wise balances: ${err}`);
      }
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setWiseSyncing(false);
    }
  };

  const handleOpenTransactions = (account) => {
    setSelectedAccount(account);
    setTransactionsDialogOpen(true);
  };

  const handleUploadCertificate = async () => {
    if (!certificateFile || !editingAccount) return;

    setCertificateUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', certificateFile);
      formData.append('bankAccountId', editingAccount.id);

      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/bank/certificate/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const result = await response.json();
      if (result.success) {
        alert('Сертификатът е качен успешно');
        setCertificateFile(null);
        refetch();
      } else {
        alert(`Грешка: ${result.message}`);
      }
    } catch (e) {
      alert(`Грешка: ${e.message}`);
    } finally {
      setCertificateUploading(false);
    }
  };

  const handleDeleteCertificate = async (accountId) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете сертификата?')) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/bank/certificate/${accountId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();
      if (result.success) {
        alert('Сертификатът е изтрит');
        refetch();
      } else {
        alert(`Грешка: ${result.message}`);
      }
    } catch (e) {
      alert(`Грешка: ${e.message}`);
    }
  };

  const validateIban = (iban) => {
    // Basic IBAN validation
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();
    const ibanRegex = /^[A-Z]{2}\d{2}[A-Z0-9]{4}\d{7}([A-Z0-9]?){0,16}$/;
    return ibanRegex.test(cleanIban);
  };

  const validateBic = (bic) => {
    // Basic BIC validation
    const cleanBic = bic.replace(/\s/g, '').toUpperCase();
    const bicRegex = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
    return bicRegex.test(cleanBic);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.bankName?.trim()) {
      newErrors.bankName = 'Наименованието на банката е задължително';
    }

    if (!formData.iban?.trim()) {
      newErrors.iban = 'IBAN е задължителен';
    } else if (!validateIban(formData.iban)) {
      newErrors.iban = 'Невалиден IBAN формат';
    } else {
      // Check for duplicate IBAN
      const existingAccount = bankAccounts.find(
        (ba) => 
          ba.iban === formData.iban?.replace(/\s/g, '').toUpperCase() && 
          ba.id !== editingAccount?.id
      );
      if (existingAccount) {
        newErrors.iban = 'Този IBAN вече се използва от друга банкова сметка';
      }
    }

    if (!formData.bic?.trim()) {
      newErrors.bic = 'BIC кодът е задължителен';
    } else if (!validateBic(formData.bic)) {
      newErrors.bic = 'Невалиден BIC формат';
    }

    if (!formData.currencyCode?.trim()) {
      newErrors.currencyCode = 'Валутата е задължителна';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const input = {
        ...formData,
        iban: formData.iban?.replace(/\s/g, '').toUpperCase(),
        bic: formData.bic?.replace(/\s/g, '').toUpperCase(),
      };

      if (editingAccount) {
        await updateBankAccount({
          variables: {
            input: {
              id: editingAccount.id,
              ...input,
            },
          },
        });
      } else {
        await createBankAccount({
          variables: {
            input: {
              ...input,
              companyId,
            },
          },
        });
      }
    } catch (err) {
      console.error('Error saving bank account:', err);
    }
  };

  const handleSetDefault = (accountId) => {
    setDefaultBankAccount({
      variables: { id: accountId },
    });
  };

  const handleToggleActive = (account) => {
    if (account.isActive) {
      deactivateBankAccount({
        variables: { id: account.id },
      });
    } else {
      activateBankAccount({
        variables: { id: account.id },
      });
    }
  };

  const getCurrencyColor = (currencyCode) => {
    switch (currencyCode) {
      case 'BGN': return 'success';
      case 'EUR': return 'primary';
      case 'USD': return 'secondary';
      case 'GBP': return 'info';
      default: return 'default';
    }
  };

  const formatIban = (iban) => {
    return iban.replace(/(.{4})/g, '$1 ').trim();
  };

  if (loading) return <Typography>Зареждане...</Typography>;
  if (error) return <Alert severity="error">Грешка: {error.message}</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BankIcon color="primary" />
          Банкови сметки
        </Typography>
        <Stack direction="row" spacing={2}>
           <Button
            variant="outlined"
            startIcon={wiseSyncing ? <CircularProgress size={20} /> : <CloudSyncIcon />}
            onClick={handleImportWiseBalances}
            disabled={wiseSyncing}
          >
            Import Wise Accounts
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Добави сметка
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Банка</TableCell>
              <TableCell>IBAN</TableCell>
              <TableCell>BIC</TableCell>
              <TableCell>Валута</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Интеграция</TableCell>
              <TableCell align="center">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bankAccounts.map((account) => (
              <TableRow key={account.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {account.bankName}
                    </Typography>
                    {account.accountName && (
                      <Typography variant="caption" color="text.secondary">
                        {account.accountName}
                      </Typography>
                    )}
                    {account.isDefault && (
                      <Chip
                        label="По подразбиране"
                        size="small"
                        color="primary"
                        icon={<StarIcon />}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {formatIban(account.iban)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {account.bic}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={account.currencyCode}
                    size="small"
                    color={getCurrencyColor(account.currencyCode)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={account.isActive ? 'Активна' : 'Неактивна'}
                    size="small"
                    color={account.isActive ? 'success' : 'default'}
                    variant={account.isActive ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {account.isWiseConnected && (
                      <Chip label="Wise" size="small" color="info" icon={<CloudSyncIcon />} />
                    )}
                    {account.hasCertificate && (
                      <Tooltip title="Има банково удостоверение">
                        <VerifiedUserIcon color="success" fontSize="small" />
                      </Tooltip>
                    )}
                    {!account.isWiseConnected && !account.hasCertificate && (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Tooltip title="Транзакции">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenTransactions(account)}
                      >
                        <ListAltIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={account.isDefault ? 'По подразбиране' : 'Задай по подразбиране'}>
                      <IconButton
                        size="small"
                        onClick={() => handleSetDefault(account.id)}
                        disabled={account.isDefault}
                      >
                        {account.isDefault ? <StarIcon color="primary" /> : <StarBorderIcon />}
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={account.isActive ? 'Деактивирай' : 'Активирай'}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(account)}
                      >
                        {account.isActive ? (
                          <ToggleOnIcon color="success" />
                        ) : (
                          <ToggleOffIcon color="disabled" />
                        )}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Редактирай">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(account)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {bankAccounts.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Няма добавени банкови сметки
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingAccount ? 'Редактиране на банкова сметка' : 'Нова банкова сметка'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Наименование на банката"
                value={formData.bankName || ''}
                onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                fullWidth
                required
                error={!!errors.bankName}
                helperText={errors.bankName}
                placeholder="Уникредит Булбанк АД"
              />
              <TextField
                label="Име на сметката"
                value={formData.accountName || ''}
                onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                fullWidth
                placeholder="Основна сметка BGN"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="IBAN"
                value={formData.iban || ''}
                onChange={(e) => setFormData({ ...formData, iban: e.target.value.replace(/\s/g, '').toUpperCase() })}
                fullWidth
                required
                error={!!errors.iban}
                helperText={errors.iban}
                placeholder="BG80BNBG96611020345678"
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
              <TextField
                label="BIC код"
                value={formData.bic || ''}
                onChange={(e) => setFormData({ ...formData, bic: e.target.value.toUpperCase() })}
                fullWidth
                required
                error={!!errors.bic}
                helperText={errors.bic}
                placeholder="UNCRBGSF"
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth required error={!!errors.currencyCode}>
                <InputLabel>Валута</InputLabel>
                <Select
                  value={formData.currencyCode || 'BGN'}
                  onChange={(e) => setFormData({ ...formData, currencyCode: e.target.value })}
                  label="Валута"
                >
                  {CURRENCY_OPTIONS.map((currency) => (
                    <MenuItem key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                label="Ред на сортиране"
                type="number"
                value={formData.sortOrder || 0}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                fullWidth
              />
            </Box>

            <TextField
              label="Описание"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="Допълнителна информация за сметката"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isDefault || false}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                />
              }
              label="По подразбиране"
            />

            {/* Bank Certificate Section - only show when editing */}
            {editingAccount && (
              <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VerifiedUserIcon color="primary" />
                  Банково удостоверение (PDF)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Прикачете банково удостоверение или документ с подпис за верификация на сметката.
                  Ще се изпраща автоматично с всяка фактура.
                </Typography>

                {editingAccount.hasCertificate ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label="Качен сертификат"
                      color="success"
                      icon={<VerifiedUserIcon />}
                    />
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteCertificate(editingAccount.id)}
                    >
                      Изтрий
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setCertificateFile(e.target.files[0])}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={certificateUploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                      onClick={handleUploadCertificate}
                      disabled={!certificateFile || certificateUploading}
                    >
                      Качи
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отказ</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingAccount ? 'Запази' : 'Създай'}
          </Button>
        </DialogActions>
      </Dialog>

      <TransactionDialog 
        open={transactionsDialogOpen} 
        onClose={() => setTransactionsDialogOpen(false)} 
        bankAccount={selectedAccount}
        companyId={companyId}
      />
    </Box>
  );
};

export default BankAccountsTab;
